"use client"

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Mention } from "@/schema/cross_reference"
import {
  badgeVariants,
  Button,
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { ArrowUpRight, X } from "lucide-react"

import { useGlobalSearch } from "@/hooks/use-global-search"
import { MentionOption } from "@/components/lexical/plugins/mention-dropdown/types"

interface MentionComboboxProps {
  mentions: Mention[]
  onChange?: (refs: Mention[]) => void
  disabled?: boolean
  placeholder?: string
}

const ALLOWED_REFERENCE_TYPES = ["client", "prospect", "person", "business", "household", "trust"] as const

export function MentionCombobox({
  mentions,
  onChange,
  disabled = false,
  placeholder = "Search for people, households, businesses...",
}: MentionComboboxProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  const { results, handleSearchChange, isSearching, clearSearch } = useGlobalSearch({
    allowedTypes: ALLOWED_REFERENCE_TYPES,
  })

  // Convert results to options format
  const searchOptions = useMemo(() => {
    return results.map((r) => {
      const mentionOption = new MentionOption(r)
      return {
        target_id: mentionOption.recordId,
        target_type: mentionOption.recordType,
        target_label: mentionOption.label,
      }
    })
  }, [results])

  // Filter out already selected items from search results
  const availableOptions = useMemo(() => {
    const selectedSet = new Set(mentions.map((r) => `${r.target_type}-${r.target_id}`))
    return searchOptions.filter((opt) => !selectedSet.has(`${opt.target_type}-${opt.target_id}`))
  }, [mentions, searchOptions])

  // Focus input when popover opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    } else {
      // Clear search when closing
      clearSearch()
      setSearchValue("")
    }
  }, [isOpen, clearSearch])

  const handleSelect = useCallback(
    (option: Mention) => {
      if (!onChange) return

      const exists = mentions.some((r) => r.target_id === option.target_id && r.target_type === option.target_type)

      if (!exists) {
        // Add the new reference
        const newRefs = [...mentions, option]
        onChange(newRefs)

        // Preserve scroll position and keep popover open
        const scrollPosition = scrollAreaRef.current?.scrollTop || 0
        setTimeout(() => {
          if (scrollAreaRef.current) {
            scrollAreaRef.current.scrollTop = scrollPosition
          }
          searchInputRef.current?.focus()
        }, 0)
      }
    },
    [mentions, onChange]
  )

  const handleRemove = useCallback(
    (reference: Mention) => {
      if (!onChange) return
      onChange(
        mentions.filter((r) => !(r.target_id === reference.target_id && r.target_type === reference.target_type))
      )
    },
    [mentions, onChange]
  )

  const handleSearchInput = useCallback(
    (value: string) => {
      setSearchValue(value)
      handleSearchChange(value)
    },
    [handleSearchChange]
  )

  const formatRecordType = (type: string): string => {
    switch (type.toLowerCase()) {
      case "person":
        return "Person"
      case "business":
        return "Business"
      case "household":
        return "Household"
      case "client":
        return "Client"
      case "prospect":
        return "Prospect"
      default:
        return type.charAt(0).toUpperCase() + type.slice(1)
    }
  }

  const formatGroupHeader = (type: string): string => {
    switch (type) {
      case "Person":
        return "People"
      case "Business":
        return "Businesses"
      default:
        return type + "s"
    }
  }

  const tooltipContent =
    mentions.length === 0
      ? "Add related records"
      : `${mentions.length} related record${mentions.length !== 1 ? "s" : ""}`

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                size="iconXs"
                variant={mentions.length > 0 ? "secondary" : "ghost"}
                disabled={disabled}
                type="button"
                aria-label="Manage cross references (alternative)"
                className="relative"
              >
                <ArrowUpRight className="text-icon h-4 w-4" />
                {mentions.length > 0 && (
                  <span
                    className={cn(
                      badgeVariants({ variant: "secondary", size: "xs" }),
                      "absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-xs"
                    )}
                  >
                    {mentions.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top" align="start">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        className="w-[350px] p-0"
        align="start"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          searchInputRef.current?.focus()
        }}
      >
        <div className="flex w-full flex-col">
          {/* Show selected items at the top with better styling */}
          {mentions.length > 0 && (
            <div className="bg-muted/30 border-b px-3 py-2">
              <div className="text-muted-foreground mb-1.5 text-xs font-medium">Selected ({mentions.length})</div>
              <div className="flex flex-wrap gap-1">
                {mentions.map((ref) => (
                  <div
                    key={`${ref.target_type}-${ref.target_id}`}
                    className="bg-primary/10 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs"
                  >
                    <span className="text-primary/70 font-medium">{formatRecordType(ref.target_type)}</span>
                    <span className="text-foreground">{ref.target_label}</span>
                    <button
                      onClick={() => handleRemove(ref)}
                      className="hover:bg-destructive/20 hover:text-destructive ml-0.5 rounded-sm"
                      aria-label={`Remove ${ref.target_label}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Command shouldFilter={false}>
            <CommandInput
              ref={searchInputRef}
              placeholder={placeholder}
              value={searchValue}
              onValueChange={handleSearchInput}
              className="border-0"
            />
            <CommandList>
              <div ref={scrollAreaRef} className="max-h-[350px] overflow-y-auto">
                {isSearching ? (
                  <div className="text-muted-foreground py-6 text-center text-sm">Searching...</div>
                ) : searchValue && availableOptions.length === 0 ? (
                  <div className="text-muted-foreground py-6 text-center text-sm">No results found</div>
                ) : !searchValue ? (
                  <div className="text-muted-foreground py-6 text-center text-sm">Start typing to search...</div>
                ) : (
                  <>
                    {/* Group available options by type */}
                    {(() => {
                      const groupedOptions: Record<string, Mention[]> = {}

                      availableOptions.forEach((opt) => {
                        const type = formatRecordType(opt.target_type)
                        if (!groupedOptions[type]) {
                          groupedOptions[type] = []
                        }
                        groupedOptions[type].push(opt)
                      })

                      return Object.entries(groupedOptions).map(([type, options]) => (
                        <div key={type} className="px-1 py-1">
                          <div className="text-muted-foreground px-2 py-1.5 text-xs font-semibold">
                            {formatGroupHeader(type)}
                          </div>
                          {options.map((option) => (
                            <CommandItem
                              key={`${option.target_type}-${option.target_id}`}
                              onSelect={() => handleSelect(option)}
                              className="cursor-pointer"
                            >
                              <span className="truncate">{option.target_label}</span>
                            </CommandItem>
                          ))}
                        </div>
                      ))
                    })()}
                  </>
                )}
              </div>
            </CommandList>
          </Command>
        </div>
      </PopoverContent>
    </Popover>
  )
}
